import React, { useCallback, useEffect, useRef } from 'react';
import MainView from './pages/MainView';
import { useAuth0, withAuthenticationRequired } from '@auth0/auth0-react';
import amplitudeLog, { initAmplitude } from './amplitude';
import { AMP_APP_RELOAD } from '@shared/constants';
import {
  detectScreenResolution,
  detectViewport,
} from '@shared/helpers/analytics.helpers';
import { useInitUIStore } from './store/ui';
import { useSocketListeners } from './hooks/use-sockets.hook';
import { getUserOrganizationNames } from '@shared/helpers/user.helpers';
import { useUser } from './hooks/use-user.hooks';
import NodonSnackbarProvider from './providers/NodonSnackbarProvider';
import { useProjectId } from './store/project';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DataLoaderProxy } from './DataLoaderProxy';
import Auth0TokenRefresher from './Auth0TokenRefresher';
import { GlobalLoader } from '@ui/GlobalLoader';
import { useInitSentry } from './hooks/use-sentry.hook';
import { useSelectedOrganizationName } from './store/organization';
import { subscribeOpenProjectRevisionStaleOnHide } from './store/project/project-revision-sync.helpers';

const App: React.FC = () => {
  const { isLoading, isAuthenticated } = useAuth0();
  const [addSocketListeners] = useSocketListeners();

  useInitUIStore();

  const user = useUser();
  const selectedOrganization = useSelectedOrganizationName();
  const socketOrganizationRef = useRef<string | undefined>(undefined);
  const projectIdRef = useRef<number | undefined>(undefined);
  const projectId = useProjectId();

  const onLogin = useCallback(async () => {
    if (user && selectedOrganization && projectId > 0) {
      await initAmplitude();
      const userGroups: Array<string> = getUserOrganizationNames(user);
      const isLoggedIn = window.sessionStorage.getItem('isLoggedIn');
      const shouldStartAmplitudeSession = !socketOrganizationRef.current;

      // Add socket listeners if they are not already added or if the organization has changed
      if (
        !socketOrganizationRef.current ||
        !projectIdRef.current ||
        socketOrganizationRef.current !== selectedOrganization ||
        projectIdRef.current !== projectId
      ) {
        await addSocketListeners();
        socketOrganizationRef.current = selectedOrganization;
        projectIdRef.current = projectId;
      }

      if (isLoggedIn === 'true') {
        return amplitudeLog(AMP_APP_RELOAD);
      }

      if (shouldStartAmplitudeSession) {
        amplitudeLog('Session Start', {
          'User Groups': userGroups,
          'Screen Resolution': detectScreenResolution(),
          Viewport: detectViewport(),
        });
      }

      window.sessionStorage.setItem('isLoggedIn', 'true');
    }
  }, [user, selectedOrganization, projectId, addSocketListeners]);

  // Initialize Sentry
  useInitSentry();

  // A hidden tab misses remote project saves. The next local save rebases
  // onto the server row instead of PUTting a stale optimistic-lock token.
  useEffect(() => subscribeOpenProjectRevisionStaleOnHide(), []);

  // Wait until the user is authenticated to run the login function
  useEffect(() => {
    if (isAuthenticated && user && selectedOrganization) {
      onLogin().catch((error) => console.error(error));
    }
  }, [isAuthenticated, onLogin, user, selectedOrganization]);

  return (
    <>
      <GlobalLoader.Register open={isLoading} text="Signing in" />
      <Auth0TokenRefresher>
        <NodonSnackbarProvider>
          <LocalizationProvider dateAdapter={AdapterLuxon}>
            <DataLoaderProxy>
              <MainView />
            </DataLoaderProxy>
          </LocalizationProvider>
        </NodonSnackbarProvider>
      </Auth0TokenRefresher>
    </>
  );
};

export default withAuthenticationRequired(App, {
  onRedirecting: () => <GlobalLoader.Register text="Signing in" />,
});
